import { useTab } from '@/common/entityFramework/entityTabs/TabContext';
import { useNavState } from '@/common/navState/NavStateContext';
import { alpha, Box, useTheme } from '@mui/material';
import { themeMaterial } from 'ag-grid-enterprise';
import { AgGridReact, getInstance } from 'ag-grid-react';
import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useKrCardContext } from '../card/KrCardContext';
import { useInDialog } from '../dialog/DialogScopeContext';
import { StringUtils } from '../utils/StringUtils';
import { ContextMenu } from './contextMenu/ContextMenu';
import { RowCountStatusBar } from './RowCountStatusBar';

export const Grid = ({
  id = 'grid',
  gridOptions = {},
  events = {}, // An object mapping AgGrid events to functions to be executed when that event fires (ex: { onFilterChanged: () => { HANDLE FILTER CHANGED }})
  ref,
}) => {
  // Vite replacement for require.context: eager loads all matching cell renderer modules
  const modules = import.meta.glob('/src/**/cellRenderer/*.jsx', {
    eager: true,
  });
  const components = Object.entries(modules).reduce((components, [key, module]) => {
    const componentParts = key.split('/');
    const componentName = componentParts[componentParts.length - 1].replace('.jsx', '');
    const component = module[componentName];
    const componentKey = StringUtils.toLowerCamel(componentName);
    // In order to reference cells via data-test-id in testing, we must wrap all the cellRenderers
    components[componentKey] = component;
    return components;
  }, {});

  // Context
  const { navState, assign } = useNavState();
  const { tabId } = useTab();

  const gridNavStateId = tabId ? tabId + StringUtils.toUpperCamel(id) + 'State' : id + 'State';

  // Ref
  const gridContainerRef = useRef(null);
  const gridApiRef = useRef(null);

  const krCardContext = useKrCardContext();
  const inKrCard = Boolean(krCardContext);

  useEffect(() => {
    if (!krCardContext?.registerGrid) return;
    krCardContext.registerGrid();
    return () => {
      krCardContext.unregisterGrid();
    };
  }, [krCardContext]);

  // TODO: eliminate this. This is a hack for GTN Demo 5.14.25
  // Must understand the closure on gridNavState in serverSideDataSource.getRows
  const navStateRef = useRef();
  useEffect(() => {
    navStateRef.current = navState;
  }, [navState]);

  // API to Parent
  useImperativeHandle(ref, () => {
    return {
      gridApiRef,
      setDataSource: (dataSource) => {
        const rowModelType = gridApiRef.current.getGridOption('rowModelType');
        if (rowModelType === 'clientSide') {
          gridApiRef.current.setGridOption('rowData', dataSource);
          restoreClientSideOpenedRowsState();
          setTimeout(() => {
            resize();
          }, 100);
        } else if (rowModelType === 'infinite') {
          gridApiRef.current.setGridOption('datasource', dataSource);
        } else if (rowModelType === 'serverSide') {
          const serverSideDataSource = {
            getRows: (params) => {
              dataSource.getRows(params).then(() => {
                // Resize Columns
                setTimeout(() => {
                  resize();
                }, 100);

                // Restore opened rows
                if (!navStateRef.current || !navStateRef.current[gridNavStateId]) {
                  return;
                }

                const gridNavState = navStateRef.current[gridNavStateId];
                if (gridNavState?.openedRowGroups) {
                  gridApiRef.current.forEachNode((node) => {
                    let expanded = node.data?.path && gridNavState.openedRowGroups.indexOf(node.data.path) !== -1;
                    if (expanded) {
                      node.setExpanded(expanded);
                    }
                  });
                }
              });
            },
          };
          gridApiRef.current.setGridOption('serverSideDatasource', serverSideDataSource);
        } else {
          console.error('The only valid grid types are clientSide, serverSide and infinite');
        }
      },
      setColDefs: (colDefs) => {
        // TODO: Remove, this should only be used by Interact for now
        gridApiRef.current.setGridOption('columnDefs', colDefs);
      },
      setCount,
      resize,
      getRowModelType: () => {
        return gridApiRef.current.getGridOption('rowModelType');
      },
      resetOpenedRowGroups: () => {
        const gridNavState = navState[gridNavStateId];
        if (!gridNavState) return;

        gridNavState.openedRowGroups = null;
        assign(gridNavStateId, gridNavState);
      },
      onGridSuccess: (rows, params) => {
        const grid = gridApiRef.current;
        if (params.startRow === 0 || grid.totalItems === undefined) {
          grid.totalItems = 0;
        }
        grid.totalItems += rows.length;
        const lastRow = rows.length < params.endRow - params.startRow ? grid.totalItems : null;

        params.successCallback(rows, lastRow);
        if (grid) {
          if (grid.totalItems === 0) {
            grid.showNoRowsOverlay();
          } else {
            grid.hideOverlay();
          }
        }
      },
      setTotalsRow: (row) => {
        gridApiRef.current.setGridOption('pinnedBottomRowData', [row]);
      },
    };
  }, [navState]);

  /**
   *  Internal Grid Methods
   */

  const resize = () => {
    const cols = gridApiRef.current.getAllGridColumns().filter((col) => col.visible);

    // 1. Size to cell contents
    gridApiRef.current.autoSizeAllColumns(false);

    if (!cols) return;

    // 2. Measure current total width
    const totalColWidth = cols.reduce((w, col) => w + col.getActualWidth(), 0);

    // 3. Compare with the width of the Grid's container
    const bodyWidth = gridContainerRef.current.getBoundingClientRect().width;

    // 4. Stretch if we still have white space
    if (totalColWidth < bodyWidth) {
      const columnLimits = cols.map((col) => {
        const minWidth = col.getActualWidth();
        return { key: col, minWidth: minWidth };
      });
      gridApiRef.current.sizeColumnsToFit({ columnLimits });
    }
  };

  const restoreGridState = () => {
    const gridNavState = navState[gridNavStateId];

    if (!gridNavState) {
      return;
    }

    gridApiRef.current.applyColumnState({
      state: gridNavState.columnState ?? [],
    });
    gridApiRef.current.setColumnGroupState(gridNavState.columnGroupState ?? []);
    gridApiRef.current.setFilterModel(gridNavState.filterModel ?? {});
  };

  const restoreClientSideOpenedRowsState = () => {
    if (!gridOptions.treeData) {
      return;
    }

    const gridNavState = navState[gridNavStateId];

    if (!gridNavState) {
      return;
    }

    gridApiRef.current.forEachNode((node) => {
      // Restore expanded state from saved gridNavState
      let expanded = node.data?.path && gridNavState.openedRowGroups?.indexOf(node.data.path) !== -1;
      node.setExpanded(expanded);
    });
  };

  const setCount = (count) => {
    if (gridApiRef.current.getGridOption('rowModelType') === 'clientSide') {
      count = gridApiRef.current.getDisplayedRowCount();
    } else if (count == null) {
      count = '-';
    }

    getInstance(gridApiRef.current.getStatusPanel('rowCountStatusBar'), (statusBarComponent) => {
      if (statusBarComponent) {
        statusBarComponent.setCount(count);
      }
    });
  };

  /**
   * Processing on Grid Initialization
   */

  const cloneFieldDefs = (fieldDefs) => {
    return fieldDefs.map((fieldDef) => ({
      ...fieldDef,
      children: fieldDef.children ? cloneFieldDefs(fieldDef.children) : undefined,
    }));
  };

  // At this point, fieldDefs become the basis of the colDef that is used by AG Grid, with slight modifications
  const fieldDefsToColDefs = (fieldDefs) => {
    const colDefs = cloneFieldDefs(fieldDefs);

    for (let i = 0; i < colDefs.length; i++) {
      const colDef = colDefs[i];

      // Type (OpenPharma FieldDef API)-> CellRenderer (AG Grid API)
      if (colDef.type && colDef.type !== 'NONE') {
        colDef.cellRenderer = StringUtils.toLowerCamel(colDef.type) + 'CellRenderer';
      }

      // Set Filters
      if (colDef.filter === 'agSetColumnFilter') {
        colDef.filterParams = {
          debounceMs: 0,
          values: colDef.values.map((enumSpec) => enumSpec.code),
          valueFormatter: ({ colDef, value }) => {
            const enumSpec = colDef.values.find((enumSpec) => enumSpec.code === value);
            return enumSpec.displayName;
          },
        };
      }

      // For foreign Entities, replace the ID column with Code. Optionally add a Name column
      if (colDef.cellRenderer === 'hyperlinkCellRenderer' && colDef.field !== 'code') {
        // Clone the original colDef so we have its unmodified values.
        const originalCopy = { ...colDef };

        // Modify the original colDef: append 'Code' to field and displayName.
        colDef.field = originalCopy.field.replace('Id', 'Code');
        colDef.displayName = originalCopy.displayName + ' Code';

        // If hasName is true, create and insert a new colDef.
        if (originalCopy.hasName) {
          const newColumnDef = {
            ...originalCopy,
            field: originalCopy.field.replace('Id', 'Name'),
            headerName: originalCopy.displayName + ' Name',
            displayName: originalCopy.displayName + ' Name',
          };

          // Insert the new colDef immediately after the original.
          colDefs.splice(i + 1, 0, newColumnDef);

          // Increment i so that we skip over the inserted colDef.
          i++;
        }
      }

      // DisplayName (OpenPharma FieldDef API)-> HeaderName (AG Grid API)
      colDef.headerName = colDef.displayName;

      if (colDef.children) {
        colDef.children = fieldDefsToColDefs(colDef.children);
      }
    }
    return colDefs;
  };

  // Merge gridOptions from the props with the defaults
  const combinedGridOptions = useMemo(() => {
    const combinedGridOptions = { ...DEFAULT_GRID_OPTIONS, ...gridOptions };

    // Process colDefs
    if (combinedGridOptions.fieldDefs) {
      const fieldDefs = combinedGridOptions.fieldDefs;
      combinedGridOptions.columnDefs = fieldDefsToColDefs(fieldDefs);
    }

    // Handle Tree Data
    if (combinedGridOptions.treeData) {
      combinedGridOptions.getDataPath = (data) => {
        if (!data.path) return;
        return data.path.toString().split('|');
      };

      if (combinedGridOptions.columnDefs) {
        const autoGroupingColumnDef = combinedGridOptions.columnDefs.filter((colDef) => colDef.autoGrouping)[0];
        autoGroupingColumnDef.rowGroup = true;
        const cellRenderer = autoGroupingColumnDef.cellRenderer;
        autoGroupingColumnDef.cellRenderer = 'agGroupCellRenderer';
        autoGroupingColumnDef.cellRendererParams = {
          suppressCount: true,
          innerRenderer: cellRenderer,
        };
      }
    }

    // Process contextMenuItems to leverage our own custom component
    const originalGetContextMenuItems = combinedGridOptions.getContextMenuItems || (() => []);
    const processContextMenuItems = (contextMenuItems) => {
      return contextMenuItems.map((contextMenuItem) => {
        const clonedContextMenuItem = { ...contextMenuItem };
        clonedContextMenuItem.menuItem = ContextMenu;
        if (clonedContextMenuItem.subMenu) {
          clonedContextMenuItem.subMenu = processContextMenuItems(clonedContextMenuItem.subMenu);
        }
        return clonedContextMenuItem;
      });
    };

    combinedGridOptions.getContextMenuItems = (params) => {
      // If the user right clicked in the grid, but not on a node, return nothing
      if (!params.node) return;

      const contextMenuItems =
        typeof originalGetContextMenuItems === 'function' ?
          originalGetContextMenuItems(params)
        : [{ name: 'No options available', disabled: true }];
      return processContextMenuItems(contextMenuItems);
    };

    return combinedGridOptions;
  }, [gridOptions]);

  // useEffect(( ) => {
  //    gridApiRef?.current?.updateGridOptions(combinedGridOptions)
  // }, [combinedGridOptions])

  /**
   * The Grid internally responds to some events, so if there is an external callback provided, we will call it after the Grid's internal processing
   */
  const replaceEventCallback = (eventName, callback) => {
    const extCallback = events[eventName];
    events[eventName] = (event) => {
      callback(event);
      if (extCallback) {
        extCallback(event);
      }
    };
  };

  replaceEventCallback('onGridReady', (event) => {
    gridApiRef.current = event.api;
    restoreGridState();
  });

  replaceEventCallback('onRowGroupOpened', (event) => {
    if (!gridOptions.treeData || !event?.data?.path) {
      return;
    }

    // serverSide tree data handles resizing after data returns from the server, not at the time the rowGroup is opened
    if (gridApiRef.current.getGridOption('rowModelType') !== 'serverSide') {
      resize();
    }

    const gridNavState = navState[gridNavStateId] ?? {};

    gridNavState.openedRowGroups = gridNavState?.openedRowGroups ?? [];
    if (event.expanded && gridNavState.openedRowGroups.indexOf(event.data?.path) === -1) {
      gridNavState.openedRowGroups.push(event.data.path);
    }
    gridNavState.openedRowGroups = gridNavState.openedRowGroups.filter(
      (e) => e !== event.data.path || (event.expanded && e === event.data.path),
    );

    assign(gridNavStateId, gridNavState);
  });

  replaceEventCallback('onFilterChanged', (event) => {
    setCount();
    updateNavState();
  });

  replaceEventCallback('onSortChanged', (event) => {
    updateNavState();
  });

  const updateNavState = () => {
    // An initialState sort fires onSortChanged before onGridReady has captured the api
    if (!gridApiRef.current) return;
    const gridNavState = navState[gridNavStateId] ?? {};

    gridNavState.columnState = gridApiRef.current.getColumnState();
    gridNavState.columnGroupState = gridApiRef.current.getColumnGroupState();
    gridNavState.filterModel = gridApiRef.current.getFilterModel();

    assign(gridNavStateId, gridNavState);
  };

  replaceEventCallback('onRowDataUpdated', (event) => {
    setCount();
  });

  replaceEventCallback('onGridSizeChanged', (event) => {
    if (!gridApiRef.current) return;
    resize();
  });

  const theme = useTheme();
  const inDialog = useInDialog();

  const gridTheme = themeMaterial.withPart().withParams({
    backgroundColor: inDialog ? theme.palette.surface.container : theme.palette.surface.low,
    checkboxCheckedBackgroundColor: theme.palette.primary.main,
    checkboxCheckedShapeColor: theme.palette.primary.contrastText,
    checkboxUncheckedBorderColor: theme.palette.divider,
    headerBackgroundColor: inDialog ? theme.palette.surface.high : theme.palette.surface.container,
    headerRowBorder: '1px solid ' + theme.palette.divider,
    headerTextColor: theme.palette.text.primary,
    menuTextColor: theme.palette.text.primary,
    rowBorder: '1px solid ' + theme.palette.divider,
    rowHoverColor: alpha(theme.palette.surface.tint, 0.08),
    selectedRowBackgroundColor: alpha(theme.palette.surface.tint, 0.24),
    statusBarLabelColor: theme.palette.text.secondary,
    statusBarValueColor: theme.palette.text.primary,
    textColor: theme.palette.text.primary,
    borderColor: theme.palette.divider,
  });

  const borderRadius = theme.shape.borderRadius;

  return (
    <Box
      id="grid-wrapper"
      ref={gridContainerRef}
      sx={{
        width: '100%',
        position: 'relative',
        flex: 1,
        minHeight: 0,
        border: inKrCard ? 'none' : '1px solid',
        borderTop: inKrCard ? '1px solid' : undefined,
        borderColor: 'divider',
        borderRadius: inKrCard ? `0px 0px ${borderRadius}px ${borderRadius}px ` : 1,
        overflow: 'hidden',
      }}
    >
      <AgGridReact
        theme={gridTheme}
        components={components}
        gridOptions={combinedGridOptions}
        containerStyle={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        {...events}
      />
    </Box>
  );
};

export const DEFAULT_GRID_OPTIONS = {
  rowModelType: 'infinite',
  defaultColDef: {
    filter: 'agTextColumnFilter',
    filterParams: {
      trimInput: true,
      debounceMs: 1000,
    },
    width: 100,
    sortable: true,
    resizable: true,
    menuTabs: ['filterMenuTab'],
  },
  suppressColumnVirtualisation: true,
  resetRowDataOnUpdate: true,
  rowHeight: 34,
  headerHeight: 42,
  suppressDragLeaveHidesColumns: true,
  treeData: false,
  groupDisplayType: 'custom',
  rowBuffer: 2,
  statusBar: {
    statusPanels: [
      {
        statusPanel: RowCountStatusBar,
        key: 'rowCountStatusBar',
        align: 'left',
      },
    ],
  },

  // TODO: deprecated properties
  rowSelection: 'multiple',
  suppressRowClickSelection: false,
  suppressCopyRowsToClipboard: true,
  enableRangeSelection: true,
  suppressPropertyNamesCheck: true,
};
